import { useCallback, useReducer, useEffect, useRef } from "react"
import { calculateMinWidth } from "src/utils/table-render-functions"
import dayjs from "dayjs"
import { DATE_FORMAT } from "src/utils/constants"
import { formatCpfCnpj } from "src/utils/format-cpf-cnpj"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
    openModal: false,
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        case "SET_MODAL":
            return { ...state, openModal: action.payload }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

export const useUltimoEventoCreditoDevedor = (
    emissaoService,
    cpfCnpj,
    dadosEmpresa,
    cetipIsin
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cpfCnpj)

    useEffect(() => {
        if (loadedRef.current === cpfCnpj) return
        loadedRef.current = cpfCnpj

        // limpa tudo se o CPF mudar
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
    }, [cpfCnpj])

    const buildTable = useCallback((dadosCabecalho) => {
        const { razaoSocial, cpf, grupoEconomico } = dadosCabecalho || {}

        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `Razão Social: ${razaoSocial || "N/A"}`,
                        `CNPJ: ${cpf ? formatCpfCnpj(cpf) : "N/A"}`,
                        `Grupo Econômico: ${grupoEconomico || "N/A"}`,
                    ],
                },
            ],
        })
    }, [])

    const loadTableRows = useCallback(async () => {
        // Se não tiver CPF, avisa o usuário e para.
        if (!cpfCnpj) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "CPFCNPJ não informado.", 
                    type: "warning",
                },
            })
            // Monta cabeçalho vazio por precaução
            buildTable({
                razaoSocial: dadosEmpresa?.Devedor || dadosEmpresa?.RazaoSocial,
                cpf: dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                grupoEconomico: dadosEmpresa?.GrupoEconomico
            })
            // Garante que o modal abre (para ver o alerta) mas sem dados
            dispatch({ type: "SET_ROWS", payload: [] })
            dispatch({ type: "SET_MODAL", payload: true }) 
            return
        }

        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.eventoCreditoDevedor(
                "get",
                `?CPFCNPJ=${cpfCnpj.toUpperCase()}`
            )
            
            // Tratamento preventivo caso o array venha direto no body ou dentro de results
            const listaResultados = response?.body?.results || (Array.isArray(response?.body) ? response.body : [])

            if (!listaResultados || listaResultados.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CPF/CNPJ consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })
                
                // Monta tabela com fallback usando os dados locais
                buildTable({
                    razaoSocial: dadosEmpresa?.Devedor || dadosEmpresa?.RazaoSocial,
                    cpf: cpfCnpj || dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                    grupoEconomico: dadosEmpresa?.GrupoEconomico
                })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                const rows = ordenaPorDataReferencia(listaResultados, "desc").map(
                    (obj, index) => ({ ...obj, id: index })
                )
                
                const primeiraLinha = rows[0] || {}

                // Monta a tabela priorizando os dados da API
                buildTable({
                    razaoSocial: primeiraLinha.Devedor || primeiraLinha.devedor || primeiraLinha.RazaoSocial || dadosEmpresa?.Devedor || dadosEmpresa?.RazaoSocial,
                    cpf: primeiraLinha.CPFCNPJ || primeiraLinha.CPF_CNPJ_Devedor || cpfCnpj || dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                    grupoEconomico: primeiraLinha.GrupoEconomico || primeiraLinha.grupoEconomico || dadosEmpresa?.GrupoEconomico
                })

                dispatch({ type: "SET_ROWS", payload: rows })
            }
            
            // Abre o modal após carregar
            dispatch({ type: "SET_MODAL", payload: true })

        } catch (error) {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao buscar dados ou CPF/CNPJ não encontrado.",
                    type: "warning", 
                },
            })
            dispatch({ type: "SET_ROWS", payload: [] })
            
            // Monta a tabela com os dados que temos, mesmo após o erro
            buildTable({
                razaoSocial: dadosEmpresa?.Devedor || dadosEmpresa?.RazaoSocial,
                cpf: cpfCnpj || dadosEmpresa?.CPF_CNPJ_Devedor || dadosEmpresa?.CPFCNPJ,
                grupoEconomico: dadosEmpresa?.GrupoEconomico
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [cpfCnpj, emissaoService, buildTable, dadosEmpresa])

    const deleteRow = useCallback(
        async (row) => {
            const { ID } = row
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.eventoCreditoDevedor("delete", ID)
                await loadTableRows()
            } catch (error) {
                 dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao deletar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0]
        

        if (!lastItem) {
            lastItem = {
                DataReferencia: dayjs().format(DATE_FORMAT),
                DataValidade: dayjs().add(1, 'year').format(DATE_FORMAT), 
                CETIP: cetipIsin, 
                CPFCNPJ: cpfCnpj,
            }
        }
        console.log("--- DEBUG: Objeto a ser enviado (POST):", lastItem)

        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.eventoCreditoDevedor("post", lastItem)
            console.log("--- DEBUG: Sucesso no POST. Recarregando tabela... ---")
            await loadTableRows()
        } catch(error) {
            console.error("--- DEBUG: Erro no POST ---", error)
             dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, cpfCnpj, emissaoService, loadTableRows])

    const updateRow = useCallback(
        async (newRow) => {
            const { ID } = newRow
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.eventoCreditoDevedor("patch", ID, newRow)
            } catch(error) {
                 dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Erro ao atualizar registro.",
                        type: "error",
                    },
                })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
            return newRow
        },
        [emissaoService]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )
    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthEvento:
                calculateMinWidth(state.rows, "EventoCreditoDevedor") / 2,
        },
        actions: {
            loadTableRows,
            deleteRow,
            addRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
            closeModal: () => dispatch({ type: "SET_MODAL", payload: false }),
        },
    }
}